'use client';
import { assertNever, console } from '@liveblocks/core';
import { useMarkInboxNotificationAsRead, useDeleteInboxNotification, useInboxNotificationThread, useRoomInfo } from '@liveblocks/react';
import { Slot } from '@radix-ui/react-slot';
import { TooltipProvider } from '@radix-ui/react-tooltip';
import React__default, { forwardRef, useState, useCallback, useMemo } from 'react';
import { useComponents } from '../components.mjs';
import { CheckIcon } from '../icons/Check.mjs';
import { DeleteIcon } from '../icons/Delete.mjs';
import { EllipsisIcon } from '../icons/Ellipsis.mjs';
import { WarningIcon } from '../icons/Warning.mjs';
import { useOverrides } from '../overrides.mjs';
import { Timestamp } from '../primitives/Timestamp.mjs';
import { useCurrentUserId } from '../shared.mjs';
import { classNames } from '../utils/class-names.mjs';
import { generateURL } from '../utils/url.mjs';
import { Avatar } from './internal/Avatar.mjs';
import { Button } from './internal/Button.mjs';
import { Dropdown, DropdownItem } from './internal/Dropdown.mjs';
import { generateInboxNotificationThreadContents, InboxNotificationComment, INBOX_NOTIFICATION_THREAD_MAX_COMMENTS } from './internal/InboxNotificationThread.mjs';
import { List } from './internal/List.mjs';
import { Room } from './internal/Room.mjs';
import { Tooltip } from './internal/Tooltip.mjs';
import { User } from './internal/User.mjs';
import { DropdownMenuTrigger } from '@radix-ui/react-dropdown-menu';

const InboxNotificationLayout = forwardRef(
  ({
    inboxNotification,
    children,
    aside,
    title,
    date,
    unread,
    markAsReadOnClick,
    onClick,
    href,
    showActions,
    overrides,
    components,
    className,
    asChild,
    ...props
  }, forwardedRef) => {
    const $ = useOverrides(overrides);
    const { Anchor } = useComponents(components);
    const Component = asChild ? Slot : Anchor;
    const [isMoreActionOpen, setMoreActionOpen] = useState(false);
    const markInboxNotificationAsRead = useMarkInboxNotificationAsRead();
    const deleteInboxNotification = useDeleteInboxNotification();
    const handleClick = useCallback(
      (event) => {
        onClick?.(event);
        const shouldMarkAsReadOnClick = markAsReadOnClick ?? Boolean(href);
        if (unread && shouldMarkAsReadOnClick) {
          markInboxNotificationAsRead(inboxNotification.id);
        }
      },
      [
        href,
        inboxNotification.id,
        markAsReadOnClick,
        markInboxNotificationAsRead,
        onClick,
        unread
      ]
    );
    const stopPropagation = useCallback((event) => {
      event.stopPropagation();
    }, []);
    const preventDefaultAndStopPropagation = useCallback(
      (event) => {
        event.preventDefault();
        event.stopPropagation();
      },
      []
    );
    const handleMoreClick = useCallback((event) => {
      event.preventDefault();
      event.stopPropagation();
      setMoreActionOpen((open) => !open);
    }, []);
    const handleMarkAsRead = useCallback(() => {
      markInboxNotificationAsRead(inboxNotification.id);
    }, [inboxNotification.id, markInboxNotificationAsRead]);
    const handleDelete = useCallback(() => {
      deleteInboxNotification(inboxNotification.id);
    }, [inboxNotification.id, deleteInboxNotification]);
    return /* @__PURE__ */ React__default.createElement(TooltipProvider, null, /* @__PURE__ */ React__default.createElement(Component, {
      className: classNames(
        "lb-root lb-inbox-notification",
        showActions === "hover" && "lb-inbox-notification:show-actions-hover",
        isMoreActionOpen && "lb-inbox-notification:action-open",
        className
      ),
      dir: $.dir,
      "data-unread": unread ? "" : void 0,
      "data-kind": inboxNotification.kind,
      onClick: handleClick,
      href,
      ...props,
      ref: forwardedRef
    }, aside && /* @__PURE__ */ React__default.createElement("div", {
      className: "lb-inbox-notification-aside"
    }, aside), /* @__PURE__ */ React__default.createElement("div", {
      className: "lb-inbox-notification-content"
    }, /* @__PURE__ */ React__default.createElement("div", {
      className: "lb-inbox-notification-header"
    }, /* @__PURE__ */ React__default.createElement("span", {
      className: "lb-inbox-notification-title"
    }, title), /* @__PURE__ */ React__default.createElement("div", {
      className: "lb-inbox-notification-details"
    }, /* @__PURE__ */ React__default.createElement("span", {
      className: "lb-inbox-notification-details-labels"
    }, /* @__PURE__ */ React__default.createElement(Timestamp, {
      locale: $.locale,
      date,
      className: "lb-date lb-inbox-notification-date"
    }), unread && /* @__PURE__ */ React__default.createElement("span", {
      className: "lb-inbox-notification-unread-indicator",
      role: "presentation"
    }))), showActions && /* @__PURE__ */ React__default.createElement("div", {
      className: "lb-inbox-notification-actions"
    }, /* @__PURE__ */ React__default.createElement(Dropdown, {
      open: isMoreActionOpen,
      onOpenChange: setMoreActionOpen,
      align: "end",
      content: /* @__PURE__ */ React__default.createElement(React__default.Fragment, null, unread ? /* @__PURE__ */ React__default.createElement(DropdownItem, {
        onSelect: handleMarkAsRead,
        onClick: stopPropagation
      }, /* @__PURE__ */ React__default.createElement(CheckIcon, {
        className: "lb-dropdown-item-icon"
      }), $.INBOX_NOTIFICATION_MARK_AS_READ) : null, /* @__PURE__ */ React__default.createElement(DropdownItem, {
        onSelect: handleDelete,
        onClick: stopPropagation
      }, /* @__PURE__ */ React__default.createElement(DeleteIcon, {
        className: "lb-dropdown-item-icon"
      }), $.INBOX_NOTIFICATION_DELETE))
    }, /* @__PURE__ */ React__default.createElement(Tooltip, {
      content: $.INBOX_NOTIFICATION_MORE
    }, /* @__PURE__ */ React__default.createElement(DropdownMenuTrigger, {
      asChild: true
    }, /* @__PURE__ */ React__default.createElement(Button, {
      className: "lb-inbox-notification-action",
      onClick: handleMoreClick,
      onPointerDown: preventDefaultAndStopPropagation,
      onPointerUp: preventDefaultAndStopPropagation,
      "aria-label": $.INBOX_NOTIFICATION_MORE
    }, /* @__PURE__ */ React__default.createElement(EllipsisIcon, {
      className: "lb-button-icon"
    }))))))), /* @__PURE__ */ React__default.createElement("div", {
      className: "lb-inbox-notification-body"
    }, children))));
  }
);
function InboxNotificationIcon({
  className,
  ...props
}) {
  return /* @__PURE__ */ React__default.createElement("div", {
    className: classNames("lb-inbox-notification-icon", className),
    ...props
  });
}
function InboxNotificationAvatar({
  className,
  ...props
}) {
  return /* @__PURE__ */ React__default.createElement(Avatar, {
    className: classNames("lb-inbox-notification-avatar", className),
    ...props
  });
}
const InboxNotificationThread = forwardRef(
  ({
    inboxNotification,
    href,
    showRoomName = true,
    showReactions = true,
    showAttachments = true,
    showActions = "hover",
    overrides,
    ...props
  }, forwardedRef) => {
    const $ = useOverrides(overrides);
    const thread = useInboxNotificationThread(inboxNotification.id);
    const currentUserId = useCurrentUserId();
    const { info } = useRoomInfo(inboxNotification.roomId);
    const contents = useMemo(() => {
      const contents2 = generateInboxNotificationThreadContents(
        inboxNotification,
        thread,
        currentUserId ?? ""
      );
      if (contents2.comments.length === 0 || contents2.userIds.length === 0) {
        return null;
      }
      switch (contents2.type) {
        case "comments": {
          const reversedUserIds = [...contents2.userIds].reverse();
          const firstUserId = reversedUserIds[0];
          const aside2 = /* @__PURE__ */ React__default.createElement(InboxNotificationAvatar, {
            userId: firstUserId
          });
          const title2 = $.INBOX_NOTIFICATION_THREAD_COMMENTS_LIST(
            /* @__PURE__ */ React__default.createElement(List, {
              values: reversedUserIds.map((userId) => /* @__PURE__ */ React__default.createElement(User, {
                key: userId,
                userId,
                replaceSelf: true
              })),
              formatRemaining: $.LIST_REMAINING_USERS,
              truncate: INBOX_NOTIFICATION_THREAD_MAX_COMMENTS - 1,
              locale: $.locale
            }),
            showRoomName ? /* @__PURE__ */ React__default.createElement(Room, {
              roomId: thread.roomId
            }) : void 0,
            reversedUserIds.length
          );
          const content2 = /* @__PURE__ */ React__default.createElement("div", {
            className: "lb-inbox-notification-comments"
          }, contents2.comments.map((comment) => /* @__PURE__ */ React__default.createElement(InboxNotificationComment, {
            key: comment.id,
            comment,
            showHeader: contents2.comments.length > 1,
            showAttachments,
            showReactions,
            overrides
          })));
          return {
            unread: contents2.unread,
            date: contents2.date,
            aside: aside2,
            title: title2,
            content: content2,
            threadId: thread.id,
            commentId: contents2.comments[contents2.comments.length - 1].id
          };
        }
        case "mention": {
          const mentionUserId = contents2.userIds[0];
          const mentionComment = contents2.comments[0];
          const aside2 = /* @__PURE__ */ React__default.createElement(InboxNotificationAvatar, {
            userId: mentionUserId
          });
          const title2 = $.INBOX_NOTIFICATION_THREAD_MENTION(
            /* @__PURE__ */ React__default.createElement(User, {
              key: mentionUserId,
              userId: mentionUserId
            }),
            showRoomName ? /* @__PURE__ */ React__default.createElement(Room, {
              roomId: thread.roomId
            }) : void 0
          );
          const content2 = /* @__PURE__ */ React__default.createElement("div", {
            className: "lb-inbox-notification-comments"
          }, /* @__PURE__ */ React__default.createElement(InboxNotificationComment, {
            key: mentionComment.id,
            comment: mentionComment,
            showHeader: false,
            showAttachments,
            showReactions,
            overrides
          }));
          return {
            unread: contents2.unread,
            date: contents2.date,
            aside: aside2,
            title: title2,
            content: content2,
            threadId: thread.id,
            commentId: mentionComment.id
          };
        }
        default:
          return assertNever(
            contents2,
            "Unexpected thread inbox notification type"
          );
      }
    }, [
      $,
      currentUserId,
      inboxNotification,
      overrides,
      showRoomName,
      showAttachments,
      showReactions,
      thread
    ]);
    const resolvedHref = useMemo(() => {
      const resolvedHref2 = href ?? info?.url;
      return resolvedHref2 ? generateURL(resolvedHref2, void 0, contents?.commentId) : void 0;
    }, [contents?.commentId, href, info?.url]);
    if (!contents) {
      return null;
    }
    const { aside, title, content, date, unread } = contents;
    return /* @__PURE__ */ React__default.createElement(InboxNotificationLayout, {
      inboxNotification,
      aside,
      title,
      date,
      unread,
      overrides,
      href: resolvedHref,
      showActions,
      markAsReadOnClick: false,
      ...props,
      ref: forwardedRef
    }, content);
  }
);
const InboxNotificationTextMention = forwardRef(
  ({
    inboxNotification,
    showActions = "hover",
    showRoomName = true,
    overrides,
    ...props
  }, ref) => {
    const $ = useOverrides(overrides);
    const unread = useMemo(() => {
      return !inboxNotification.readAt || inboxNotification.notifiedAt > inboxNotification.readAt;
    }, [inboxNotification.notifiedAt, inboxNotification.readAt]);
    return /* @__PURE__ */ React__default.createElement(InboxNotificationLayout, {
      inboxNotification,
      aside: /* @__PURE__ */ React__default.createElement(InboxNotificationAvatar, {
        userId: inboxNotification.createdBy
      }),
      title: $.INBOX_NOTIFICATION_TEXT_MENTION(
        /* @__PURE__ */ React__default.createElement(User, {
          key: inboxNotification.createdBy,
          userId: inboxNotification.createdBy
        }),
        showRoomName ? /* @__PURE__ */ React__default.createElement(Room, {
          roomId: inboxNotification.roomId
        }) : void 0
      ),
      date: inboxNotification.notifiedAt,
      unread,
      overrides,
      showActions,
      ...props,
      ref
    });
  }
);
const InboxNotificationCustom = forwardRef(
  ({
    inboxNotification,
    showActions = "hover",
    title,
    aside,
    children,
    overrides,
    ...props
  }, forwardedRef) => {
    const unread = useMemo(() => {
      return !inboxNotification.readAt || inboxNotification.notifiedAt > inboxNotification.readAt;
    }, [inboxNotification.notifiedAt, inboxNotification.readAt]);
    return /* @__PURE__ */ React__default.createElement(InboxNotificationLayout, {
      inboxNotification,
      aside,
      title,
      date: inboxNotification.notifiedAt,
      unread,
      overrides,
      showActions,
      ...props,
      ref: forwardedRef
    }, children);
  }
);
const InboxNotificationCustomMissing = forwardRef(({ inboxNotification, ...props }, forwardedRef) => {
  return /* @__PURE__ */ React__default.createElement(InboxNotificationCustom, {
    inboxNotification,
    ...props,
    title: /* @__PURE__ */ React__default.createElement(React__default.Fragment, null, "Custom notification kind ", /* @__PURE__ */ React__default.createElement("code", null, inboxNotification.kind), " is not handled"),
    aside: /* @__PURE__ */ React__default.createElement(InboxNotificationIcon, null, /* @__PURE__ */ React__default.createElement(WarningIcon, null)),
    ref: forwardedRef,
    "data-missing": ""
  }, "Notifications of this kind won\u2019t be displayed in production. Use the", " ", /* @__PURE__ */ React__default.createElement("code", null, "kinds"), " prop to define how they should be rendered.");
});
const inboxNotificationKindsWarnings = /* @__PURE__ */ new Set();
const InboxNotification = Object.assign(
  forwardRef(
    ({ inboxNotification, kinds, ...props }, forwardedRef) => {
      switch (inboxNotification.kind) {
        case "thread": {
          const ResolvedInboxNotificationThread = kinds?.thread ?? InboxNotificationThread;
          return /* @__PURE__ */ React__default.createElement(ResolvedInboxNotificationThread, {
            inboxNotification,
            ...props,
            ref: forwardedRef
          });
        }
        case "textMention": {
          const ResolvedInboxNotificationTextMention = kinds?.textMention ?? InboxNotificationTextMention;
          return /* @__PURE__ */ React__default.createElement(ResolvedInboxNotificationTextMention, {
            inboxNotification,
            ...props,
            ref: forwardedRef
          });
        }
        default: {
          const ResolvedInboxNotificationCustom = kinds?.[inboxNotification.kind];
          if (!ResolvedInboxNotificationCustom) {
            if (process.env.NODE_ENV !== "production") {
              if (!inboxNotificationKindsWarnings.has(inboxNotification.kind)) {
                inboxNotificationKindsWarnings.add(inboxNotification.kind);
                console.warn(
                  `Custom notification kind "${inboxNotification.kind}" is not handled so notifications of this kind will not be displayed in production. Use the kinds prop to define how they should be rendered.`
                );
              }
              return /* @__PURE__ */ React__default.createElement(InboxNotificationCustomMissing, {
                inboxNotification,
                ...props,
                ref: forwardedRef
              });
            } else {
              return null;
            }
          }
          return /* @__PURE__ */ React__default.createElement(ResolvedInboxNotificationCustom, {
            inboxNotification,
            ...props,
            ref: forwardedRef
          });
        }
      }
    }
  ),
  {
    Thread: InboxNotificationThread,
    TextMention: InboxNotificationTextMention,
    Custom: InboxNotificationCustom,
    Icon: InboxNotificationIcon,
    Avatar: InboxNotificationAvatar
  }
);

export { InboxNotification };
//# sourceMappingURL=InboxNotification.mjs.map
